import * as React from "react";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Spinner,
  Text,
  makeStyles,
  tokens,
  Title3,
  Tooltip,
  Checkbox,
  Badge,
  Caption1,
} from "@fluentui/react-components";
import {
  ArrowSync24Regular,
  Checkmark16Regular,
  CheckmarkCircle16Regular,
  DismissCircle16Regular,
  DocumentEdit16Regular,
  DocumentLink16Regular,
  Delete16Regular,
  Table16Regular,
  TableAdd16Regular,
  TableInsertRow16Regular,
  TableInsertColumn16Regular,
  PaintBrush16Regular,
  ArrowRight16Regular,
  ChevronDown16Regular,
  ChevronRight16Regular,
} from "@fluentui/react-icons";
import { useWorkflowIntelligence } from "../../contexts/WorkflowIntelligenceContext";
import { getCellChanges, CellChangeDto } from "../../services/cell-changes.service";
import { ExcelService } from "../../services/excel.service";
import { formatRelativeTime } from "../../utils/formatters";
import { useTypographyStyles } from "../../styles/typography.styles";

const APPLIED_CHANGES_KEY = "WFI_AppliedCellChanges";
const DECLINED_CHANGES_KEY = "WFI_DeclinedCellChanges";

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingVerticalL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    height: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  controls: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  stats: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: tokens.spacingVerticalXXXL,
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalL,
    textAlign: "center",
    color: tokens.colorPaletteRedForeground1,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXXL,
    color: tokens.colorNeutralForeground3,
  },
  listContainer: {
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  groupCard: {
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: "hidden",
    // Prevent flex shrink — without this, cards squish vertically when total
    // content exceeds viewport height instead of overflowing into the scrollable list.
    flexShrink: 0,
  },
  groupCardDone: {
    opacity: 0.5,
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground3,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
  },
  groupTitle: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  groupTimestamp: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  groupActions: {
    display: "flex",
    gap: "2px",
    flexShrink: 0,
  },
  changeList: {
    display: "flex",
    flexDirection: "column",
  },
  changeCard: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderTopColor: tokens.colorNeutralStroke3,
  },
  changeCardApplied: {
    opacity: 0.5,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  changeCardDeclined: {
    opacity: 0.4,
    textDecoration: "line-through",
    backgroundColor: tokens.colorNeutralBackground3,
  },
  operationIcon: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  changeBody: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    flex: 1,
    minWidth: 0,
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  operationLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  location: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  deltaRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
  },
  cellValue: {
    fontFamily: "monospace",
    fontSize: tokens.fontSizeBase200,
    backgroundColor: tokens.colorNeutralBackground4,
    padding: `0 ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    maxWidth: "120px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cellValueEmpty: {
    fontFamily: "monospace",
    fontSize: tokens.fontSizeBase200,
    backgroundColor: tokens.colorNeutralBackground4,
    padding: `0 ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    color: tokens.colorNeutralForeground4,
    fontStyle: "italic",
  },
  arrowIcon: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  reason: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  actionArea: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  appliedIcon: {
    display: "flex",
    alignItems: "center",
    color: tokens.colorPaletteGreenForeground1,
  },
  declinedIcon: {
    display: "flex",
    alignItems: "center",
    color: tokens.colorPaletteRedForeground1,
  },
  progressText: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorBrandForeground1,
  },
});

// --- Excel settings helpers ---

async function loadIdSet(key: string): Promise<Set<number>> {
  try {
    return await Excel.run(async (context) => {
      const item = context.workbook.settings.getItemOrNullObject(key);
      item.load("value");
      await context.sync();
      if (item.isNullObject) return new Set<number>();
      return new Set<number>(JSON.parse(item.value));
    });
  } catch {
    return new Set<number>();
  }
}

async function saveIdSet(key: string, ids: Set<number>): Promise<void> {
  await Excel.run(async (context) => {
    context.workbook.settings.add(key, JSON.stringify(Array.from(ids)));
    await context.sync();
  });
}

// --- Formatting helpers ---

function getOperationLabel(op: string): string {
  switch (op) {
    case "set_cell": return "Set Cell";
    case "set_range": return "Set Range";
    case "clear_range": return "Clear";
    case "set_document_hyperlink": return "Document Link";
    case "set_depreciation_rate_hyperlink": return "Rate Link";
    case "create_worksheet": return "New Sheet";
    case "insert_row": return "Insert Row";
    case "insert_column": return "Insert Column";
    case "set_formatting": return "Format";
    case "set_column_width": return "Column Width";
    case "autofit_columns": return "Autofit Columns";
    case "set_row_height": return "Row Height";
    case "autofit_rows": return "Autofit Rows";
    default: return op;
  }
}

function getOperationIcon(op: string): React.ReactElement {
  switch (op) {
    case "set_cell": return <DocumentEdit16Regular />;
    case "set_range": return <Table16Regular />;
    case "clear_range": return <Delete16Regular />;
    case "set_document_hyperlink": return <DocumentLink16Regular />;
    case "set_depreciation_rate_hyperlink": return <DocumentLink16Regular />;
    case "create_worksheet": return <TableAdd16Regular />;
    case "insert_row": return <TableInsertRow16Regular />;
    case "insert_column": return <TableInsertColumn16Regular />;
    case "set_formatting": return <PaintBrush16Regular />;
    case "set_column_width": return <TableInsertColumn16Regular />;
    case "autofit_columns": return <TableInsertColumn16Regular />;
    case "set_row_height": return <TableInsertRow16Regular />;
    case "autofit_rows": return <TableInsertRow16Regular />;
    default: return <DocumentEdit16Regular />;
  }
}

/**
 * Extracts the URL and display text out of an `=HYPERLINK("url","display")`
 * formula string. Returns null if the shape doesn't match. Handles Excel's
 * double-double-quote escaping (`""` → `"`) inside either argument.
 *
 * Used when applying hyperlink cell changes: writing via Excel's native
 * hyperlink API (`range.hyperlink`) is more robust than pasting the formula
 * text, particularly for URLs containing `#` — Excel's HYPERLINK formula
 * treats `#` in link_location as an internal-reference delimiter and errors
 * on formulas like `=HYPERLINK("https://foo.com/x.pdf#page=6", ...)`.
 */
function parseHyperlinkFormula(formula: string): { url: string; displayName: string } | null {
  const match = formula.trim().match(/^=?HYPERLINK\(\s*"((?:[^"]|"")*)"\s*,\s*"((?:[^"]|"")*)"\s*\)$/i);
  if (!match) return null;
  return {
    url: match[1].replace(/""/g, '"'),
    displayName: match[2].replace(/""/g, '"'),
  };
}

/**
 * Parses the JSON stored in an `insert_row` cell change's value. Missing
 * or malformed values fall back to a single row at row 1 rather than
 * throwing — the reviewer can still Decline if that's not what they want.
 */
function parseInsertRowValue(value: string | null): { rowNumber: number; count: number } {
  if (!value) return { rowNumber: 1, count: 1 };
  try {
    const parsed = JSON.parse(value) as { rowNumber?: number; count?: number };
    return {
      rowNumber: typeof parsed.rowNumber === "number" && parsed.rowNumber >= 1 ? parsed.rowNumber : 1,
      count: typeof parsed.count === "number" && parsed.count >= 1 ? parsed.count : 1,
    };
  } catch {
    return { rowNumber: 1, count: 1 };
  }
}

/**
 * Parses the JSON stored in an `insert_column` cell change's value. If the
 * JSON is missing or malformed, falls back to the letter that leads
 * `cellAddress` (e.g. `A1` → `A`) — the CellAddress column always carries
 * the anchor, so this recovers gracefully.
 */
function parseInsertColumnValue(value: string | null, cellAddress: string): { columnLetter: string; count: number } {
  const fallbackLetter = (cellAddress.match(/^([A-Z]+)/i)?.[1] ?? "A").toUpperCase();
  if (!value) return { columnLetter: fallbackLetter, count: 1 };
  try {
    const parsed = JSON.parse(value) as { columnLetter?: string; count?: number };
    return {
      columnLetter: typeof parsed.columnLetter === "string" && /^[A-Z]+$/i.test(parsed.columnLetter)
        ? parsed.columnLetter.toUpperCase()
        : fallbackLetter,
      count: typeof parsed.count === "number" && parsed.count >= 1 ? parsed.count : 1,
    };
  } catch {
    return { columnLetter: fallbackLetter, count: 1 };
  }
}

/**
 * Formatting options shape stored on `set_formatting` cell changes. All
 * fields optional — only supplied properties get applied. Returns null on
 * malformed JSON so the caller can skip the apply instead of throwing.
 */
interface FormattingOptions {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  fontColor?: string;
  fillColor?: string;
  fontSize?: number;
  numberFormat?: string;
  horizontalAlignment?: string;
}

function parseFormattingValue(value: string | null): FormattingOptions | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as FormattingOptions;
  } catch {
    return null;
  }
}

/**
 * Parses `set_column_width` / `set_row_height` payload — `{ width }` or
 * `{ height }`. Returns null on missing / non-number so the caller can skip
 * the apply rather than pushing an invalid Excel resize.
 */
function parseResizeValue(value: string | null, field: "width" | "height"): number | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    const raw = parsed[field];
    return typeof raw === "number" && raw >= 0 ? raw : null;
  } catch {
    return null;
  }
}

function formatNewValue(change: CellChangeDto): string {
  if (change.operation === "clear_range") return "";
  if (change.operation === "set_range" && change.value) {
    try {
      const parsed = JSON.parse(change.value) as string[][];
      const maxCols = parsed.reduce((max, row) => Math.max(max, row.length), 0);
      return `${parsed.length}x${maxCols} data`;
    } catch { return change.value; }
  }
  return change.value ?? "";
}

type ChangeStatus = "pending" | "applied" | "declined";

interface TaskGroup {
  taskName: string;
  taskId: number;
  changes: CellChangeDto[];
  /** Earliest change createdAt — represents when the task started writing changes. */
  createdAt: string;
}

// --- Main component ---

/**
 * Reviews and applies/declines agent-driven cell edits queued for the workbook.
 * Tracks applied/declined IDs in localStorage so reviewed changes stay reviewed.
 */
const IncomingChanges: React.FC = () => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const { spreadsheetId } = useWorkflowIntelligence();

  const [changes, setChanges] = useState<CellChangeDto[]>([]);
  const [appliedIds, setAppliedIds] = useState<Set<number>>(new Set());
  const [declinedIds, setDeclinedIds] = useState<Set<number>>(new Set());
  const [currentValues, setCurrentValues] = useState<Record<number, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [applyingId, setApplyingId] = useState<number | null>(null);
  const [bulkApplyingTask, setBulkApplyingTask] = useState<number | null>(null);
  const [bulkProgress, setBulkProgress] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [showCompleted, setShowCompleted] = useState(false);

  const getStatus = useCallback((id: number): ChangeStatus => {
    if (appliedIds.has(id)) return "applied";
    if (declinedIds.has(id)) return "declined";
    return "pending";
  }, [appliedIds, declinedIds]);

  // Group changes by task — track the earliest change timestamp per group and
  // sort newest first so the most-recent task run appears at the top.
  const groups = useMemo((): TaskGroup[] => {
    const map = new Map<number, TaskGroup>();
    for (const c of changes) {
      if (!map.has(c.taskId)) {
        map.set(c.taskId, {
          taskName: c.taskName || "Unassigned",
          taskId: c.taskId,
          changes: [],
          createdAt: c.createdAt,
        });
      }
      const group = map.get(c.taskId)!;
      group.changes.push(c);
      if (c.createdAt < group.createdAt) {
        group.createdAt = c.createdAt;
      }
    }
    // ISO 8601 strings sort lexically the same as chronologically.
    // Groups sort newest-first so the most-recent run is at the top; changes
    // WITHIN a group sort oldest-first so create-worksheet lands before its
    // writes, and bulk-apply runs in the order the agent emitted them.
    for (const group of map.values()) {
      group.changes.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    }
    return Array.from(map.values()).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [changes]);

  const loadCurrentValues = useCallback(async (changesData: CellChangeDto[], applied: Set<number>, declined: Set<number>) => {
    const values: Record<number, string> = {};
    for (const change of changesData) {
      if (applied.has(change.id) || declined.has(change.id)) continue;
      if (change.operation === "set_cell") {
        try {
          const val = await ExcelService.getCellValue(change.cellAddress, change.sheetName);
          values[change.id] = val != null ? String(val) : "";
        } catch { values[change.id] = "?"; }
      }
    }
    setCurrentValues(values);
  }, []);

  const loadData = useCallback(async () => {
    if (!spreadsheetId) {
      setError("No spreadsheet ID available");
      setIsLoading(false);
      return;
    }
    try {
      const [changesData, applied, declined] = await Promise.all([
        getCellChanges(spreadsheetId),
        loadIdSet(APPLIED_CHANGES_KEY),
        loadIdSet(DECLINED_CHANGES_KEY),
      ]);
      setChanges(changesData);
      setAppliedIds(applied);
      setDeclinedIds(declined);
      setError(null);
      await loadCurrentValues(changesData, applied, declined);
    } catch (err) {
      setError(`Failed to load changes: ${(err as Error).message}`);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [spreadsheetId, loadCurrentValues]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData();
  };

  const handleApply = async (change: CellChangeDto) => {
    setApplyingId(change.id);
    try {
      // Fail fast on a workbook we can't write to, so the change isn't
      // marked applied off the back of a write that silently went
      // nowhere (or into a forked copy of the workbook).
      await ExcelService.assertWorkbookWritable("this change");

      switch (change.operation) {
        case "set_cell":
          await ExcelService.setCellValue(change.cellAddress, change.value ?? "", change.sheetName);
          break;
        case "set_range":
          if (change.value) {
            const data = JSON.parse(change.value) as (string | number)[][];
            await ExcelService.insertRange(data, change.cellAddress, change.sheetName);
          }
          break;
        case "clear_range":
          await ExcelService.clearContent(change.cellAddress, change.sheetName);
          break;
        case "set_document_hyperlink":
        case "set_depreciation_rate_hyperlink": {
          // Both hyperlink operations store an =HYPERLINK(url, display) formula
          // string. We parse it out and apply via the native hyperlink API
          // rather than pasting the formula — see parseHyperlinkFormula for
          // the reason (Excel's HYPERLINK formula chokes on URLs with `#`).
          const parsed = parseHyperlinkFormula(change.value ?? "");
          if (parsed) {
            await ExcelService.setCellHyperlink(change.cellAddress, parsed.url, parsed.displayName, change.sheetName);
          } else {
            // Legacy / unparseable — fall back to the formula path.
            await ExcelService.setCellFormula(change.cellAddress, change.value ?? "", change.sheetName);
          }
          break;
        }
        case "create_worksheet":
          await ExcelService.createWorksheet(change.sheetName);
          break;
        case "insert_row": {
          const { rowNumber, count } = parseInsertRowValue(change.value);
          await ExcelService.insertRows(change.sheetName, rowNumber, count);
          break;
        }
        case "insert_column": {
          const { columnLetter, count } = parseInsertColumnValue(change.value, change.cellAddress);
          await ExcelService.insertColumns(change.sheetName, columnLetter, count);
          break;
        }
        case "set_formatting": {
          const options = parseFormattingValue(change.value);
          if (options) {
            await ExcelService.applyFormatting(change.sheetName, change.cellAddress, options);
          }
          break;
        }
        case "set_column_width": {
          const width = parseResizeValue(change.value, "width");
          if (width !== null) {
            await ExcelService.setColumnWidth(change.sheetName, change.cellAddress, width);
          }
          break;
        }
        case "autofit_columns":
          await ExcelService.autofitColumns(change.sheetName, change.cellAddress);
          break;
        case "set_row_height": {
          const height = parseResizeValue(change.value, "height");
          if (height !== null) {
            await ExcelService.setRowHeight(change.sheetName, change.cellAddress, height);
          }
          break;
        }
        case "autofit_rows":
          await ExcelService.autofitRows(change.sheetName, change.cellAddress);
          break;
      }
      // Persist so the change isn't lost if the user closes without saving.
      await ExcelService.save();
      const next = new Set(Array.from(appliedIds));
      next.add(change.id);
      setAppliedIds(next);
      await saveIdSet(APPLIED_CHANGES_KEY, next);
    } catch (err) {
      console.error("Error applying change:", err);
    } finally {
      setApplyingId(null);
    }
  };

  const handleDecline = async (change: CellChangeDto) => {
    const next = new Set(Array.from(declinedIds));
    next.add(change.id);
    setDeclinedIds(next);
    await saveIdSet(DECLINED_CHANGES_KEY, next);
  };

  const handleBulkAccept = async (group: TaskGroup) => {
    const pending = group.changes.filter((c) => getStatus(c.id) === "pending");
    if (pending.length === 0) return;

    // Check once up front rather than per-change — a read-only workbook
    // would otherwise fail every item in the batch individually and mark
    // none of them applied, with the reason buried in the console.
    try {
      await ExcelService.assertWorkbookWritable("these changes");
    } catch (err) {
      console.error("Cannot apply changes to this workbook:", err);
      setError(err instanceof Error ? err.message : "This workbook can't be written to.");
      return;
    }

    setBulkApplyingTask(group.taskId);
    const newApplied = new Set(Array.from(appliedIds));
    let done = 0;

    for (const change of pending) {
      done++;
      setBulkProgress(`Applying ${done}/${pending.length}...`);
      try {
        switch (change.operation) {
          case "set_cell":
            await ExcelService.setCellValue(change.cellAddress, change.value ?? "", change.sheetName);
            break;
          case "set_range":
            if (change.value) {
              const data = JSON.parse(change.value) as (string | number)[][];
              await ExcelService.insertRange(data, change.cellAddress, change.sheetName);
            }
            break;
          case "clear_range":
            await ExcelService.clearContent(change.cellAddress, change.sheetName);
            break;
          case "set_document_hyperlink":
          case "set_depreciation_rate_hyperlink": {
            const parsed = parseHyperlinkFormula(change.value ?? "");
            if (parsed) {
              await ExcelService.setCellHyperlink(change.cellAddress, parsed.url, parsed.displayName, change.sheetName);
            } else {
              await ExcelService.setCellFormula(change.cellAddress, change.value ?? "", change.sheetName);
            }
            break;
          }
          case "create_worksheet":
            await ExcelService.createWorksheet(change.sheetName);
            break;
          case "insert_row": {
            const { rowNumber, count } = parseInsertRowValue(change.value);
            await ExcelService.insertRows(change.sheetName, rowNumber, count);
            break;
          }
          case "insert_column": {
            const { columnLetter, count } = parseInsertColumnValue(change.value, change.cellAddress);
            await ExcelService.insertColumns(change.sheetName, columnLetter, count);
            break;
          }
          case "set_formatting": {
            const options = parseFormattingValue(change.value);
            if (options) {
              await ExcelService.applyFormatting(change.sheetName, change.cellAddress, options);
            }
            break;
          }
          case "set_column_width": {
            const width = parseResizeValue(change.value, "width");
            if (width !== null) {
              await ExcelService.setColumnWidth(change.sheetName, change.cellAddress, width);
            }
            break;
          }
          case "autofit_columns":
            await ExcelService.autofitColumns(change.sheetName, change.cellAddress);
            break;
          case "set_row_height": {
            const height = parseResizeValue(change.value, "height");
            if (height !== null) {
              await ExcelService.setRowHeight(change.sheetName, change.cellAddress, height);
            }
            break;
          }
          case "autofit_rows":
            await ExcelService.autofitRows(change.sheetName, change.cellAddress);
            break;
        }
        newApplied.add(change.id);
      } catch (err) {
        console.error(`Error applying change ${change.id}:`, err);
      }
    }

    // Persist all of the batch's writes once at the end.
    try {
      await ExcelService.save();
    } catch (err) {
      console.error("Failed to save workbook after bulk apply:", err);
    }

    setAppliedIds(newApplied);
    await saveIdSet(APPLIED_CHANGES_KEY, newApplied);
    setBulkApplyingTask(null);
    setBulkProgress(null);
  };

  const handleBulkDecline = async (group: TaskGroup) => {
    const pending = group.changes.filter((c) => getStatus(c.id) === "pending");
    if (pending.length === 0) return;

    const next = new Set(Array.from(declinedIds));
    pending.forEach((c) => next.add(c.id));
    setDeclinedIds(next);
    await saveIdSet(DECLINED_CHANGES_KEY, next);
  };

  const toggleGroup = (taskId: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const totalPending = changes.filter((c) => getStatus(c.id) === "pending").length;
  const totalApplied = changes.filter((c) => getStatus(c.id) === "applied").length;
  const totalDeclined = changes.filter((c) => getStatus(c.id) === "declined").length;

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner label="Loading changes..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Text weight="semibold">Error loading changes</Text>
          <Text>{error}</Text>
          <Button onClick={handleRefresh}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title3>Incoming Changes</Title3>
        <Button
          appearance="subtle"
          icon={<ArrowSync24Regular />}
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          {isRefreshing ? "Refreshing..." : "Refresh"}
        </Button>
      </div>

      {changes.length > 0 && (
        <div className={styles.controls}>
          <div className={styles.stats}>
            <Text className={typography.bodySmall}>{totalPending} pending</Text>
            <Text className={typography.bodySmall}>{totalApplied} applied</Text>
            {totalDeclined > 0 && <Text className={typography.bodySmall}>{totalDeclined} declined</Text>}
          </div>
          <Checkbox
            label="Show completed"
            checked={showCompleted}
            onChange={(_e, data) => setShowCompleted(!!data.checked)}
          />
        </div>
      )}

      <div className={styles.listContainer}>
        {changes.length === 0 ? (
          <div className={styles.emptyState}>
            <DocumentEdit16Regular />
            <Text className={typography.emptyStateTitle}>No incoming changes</Text>
            <Text className={typography.emptyStateBody}>
              When AI tasks suggest cell changes, they will appear here for your review.
            </Text>
          </div>
        ) : (
          groups.map((group) => {
            const pendingCount = group.changes.filter((c) => getStatus(c.id) === "pending").length;
            const appliedCount = group.changes.filter((c) => getStatus(c.id) === "applied").length;
            const declinedCount = group.changes.filter((c) => getStatus(c.id) === "declined").length;
            const allDone = pendingCount === 0;
            const isExpanded = expandedGroups.has(group.taskId);
            const isBulkApplying = bulkApplyingTask === group.taskId;

            // Hide fully completed groups unless toggle is on
            if (allDone && !showCompleted) return null;

            return (
              <div
                key={group.taskId}
                className={`${styles.groupCard} ${allDone ? styles.groupCardDone : ""}`}
              >
                <div className={styles.groupHeader} onClick={() => toggleGroup(group.taskId)}>
                  <span>
                    {isExpanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                  </span>
                  <Text weight="semibold" className={styles.groupTitle}>
                    {group.taskName}
                  </Text>
                  <Tooltip content={new Date(group.createdAt).toLocaleString()} relationship="label">
                    <Caption1 className={styles.groupTimestamp}>
                      {formatRelativeTime(group.createdAt)}
                    </Caption1>
                  </Tooltip>
                  {pendingCount > 0 && (
                    <Badge appearance="filled" size="small" color="informative">
                      {pendingCount} pending
                    </Badge>
                  )}
                  {appliedCount > 0 && (
                    <Badge appearance="tint" size="small" color="success">
                      {appliedCount} applied
                    </Badge>
                  )}
                  {declinedCount > 0 && (
                    <Badge appearance="tint" size="small" color="danger">
                      {declinedCount} declined
                    </Badge>
                  )}
                  {isBulkApplying && (
                    <Caption1 className={styles.progressText}>{bulkProgress}</Caption1>
                  )}
                  <div className={styles.groupActions} onClick={(e) => e.stopPropagation()}>
                    {pendingCount > 0 && (
                      <>
                        <Tooltip content="Accept all pending" relationship="label">
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={isBulkApplying ? <Spinner size="extra-tiny" /> : <Checkmark16Regular />}
                            onClick={() => handleBulkAccept(group)}
                            disabled={isBulkApplying || !!bulkApplyingTask}
                          >
                            Accept All
                          </Button>
                        </Tooltip>
                        <Tooltip content="Decline all pending" relationship="label">
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={<DismissCircle16Regular />}
                            onClick={() => handleBulkDecline(group)}
                            disabled={isBulkApplying || !!bulkApplyingTask}
                          >
                            Decline All
                          </Button>
                        </Tooltip>
                      </>
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className={styles.changeList}>
                    {group.changes.map((change) => {
                      const status = getStatus(change.id);
                      const isApplying = applyingId === change.id;
                      const newValue = formatNewValue(change);
                      const currentVal = currentValues[change.id];

                      return (
                        <div
                          key={change.id}
                          className={`${styles.changeCard} ${
                            status === "applied" ? styles.changeCardApplied :
                            status === "declined" ? styles.changeCardDeclined : ""
                          }`}
                        >
                          <span className={styles.operationIcon}>
                            {getOperationIcon(change.operation)}
                          </span>

                          <div className={styles.changeBody}>
                            <div className={styles.titleRow}>
                              <Text className={styles.operationLabel}>
                                {getOperationLabel(change.operation)}
                              </Text>
                              <Text className={styles.location}>
                                {change.sheetName}!{change.cellAddress}
                              </Text>
                            </div>

                            {change.operation === "set_cell" && (
                              <div className={styles.deltaRow}>
                                {currentVal !== undefined ? (
                                  <span className={currentVal ? styles.cellValue : styles.cellValueEmpty}>
                                    {currentVal || "empty"}
                                  </span>
                                ) : (
                                  <span className={styles.cellValueEmpty}>...</span>
                                )}
                                <span className={styles.arrowIcon}>
                                  <ArrowRight16Regular />
                                </span>
                                <span className={newValue ? styles.cellValue : styles.cellValueEmpty}>
                                  {newValue || "empty"}
                                </span>
                              </div>
                            )}

                            {change.operation === "set_range" && (
                              <div className={styles.deltaRow}>
                                <span className={styles.cellValue}>{newValue}</span>
                              </div>
                            )}

                            {change.operation === "clear_range" && (
                              <div className={styles.deltaRow}>
                                <span className={styles.cellValueEmpty}>clear contents</span>
                              </div>
                            )}

                            <Tooltip content={change.reason} relationship="description">
                              <Text className={styles.reason}>{change.reason}</Text>
                            </Tooltip>
                          </div>

                          <div className={styles.actionArea}>
                            {status === "applied" ? (
                              <span className={styles.appliedIcon}>
                                <CheckmarkCircle16Regular />
                              </span>
                            ) : status === "declined" ? (
                              <span className={styles.declinedIcon}>
                                <DismissCircle16Regular />
                              </span>
                            ) : (
                              <>
                                <Tooltip content="Apply this change" relationship="label">
                                  <Button
                                    appearance="subtle"
                                    icon={isApplying ? <Spinner size="extra-tiny" /> : <Checkmark16Regular />}
                                    onClick={() => handleApply(change)}
                                    disabled={isApplying || !!bulkApplyingTask}
                                    size="small"
                                  />
                                </Tooltip>
                                <Tooltip content="Decline this change" relationship="label">
                                  <Button
                                    appearance="subtle"
                                    icon={<DismissCircle16Regular />}
                                    onClick={() => handleDecline(change)}
                                    disabled={isApplying || !!bulkApplyingTask}
                                    size="small"
                                  />
                                </Tooltip>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default IncomingChanges;
